import { useTranslation } from 'react-i18next';
import { Card, Col, Container, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { useClassesBookClassLoader } from '../provider/ClassesBookClassLoader';
import { IClassesBookClassItem, SearchParams, LessonActivity } from '../types';
import {
  TableCollapsable,
  Button,
  showDialogConfirm,
  showDialogInfo,
  addToast,
} from '@octano/global-ui';
import { useClassesBookClassColumns } from '../hooks/useClassesBookClassColumns';
import ClassesBookClassActivities from './ClassesBookClassActivities';
import GoBackButton from '../../../components/Button/GoBackButton';
import SectionDetails from './SectionDetails';
import ModalAddActivity, { ModalAddActivityMethods } from './ModalAddActivity';
import { useRef, useCallback, useState } from 'react';
import {
  downloadExcel,
  downloadReport,
  removeSectionClassActivity,
} from '../api';
import { useParams } from 'react-router-dom';
import { downloadFromBlob } from '../../../utils/blob';

export interface HandleSearch {
  searchParams: SearchParams<string>;
  page: number;
}

const ClassesBookEdit = () => {
  const { sectionId } = useParams<{ sectionId: string }>();

  const modalAddRef = useRef<ModalAddActivityMethods>(null);
  const { t } = useTranslation(TranslationsKeys.CLASSES_BOOK_EDIT);
  const { data, loading, refresh } = useClassesBookClassLoader();

  const [removing, setRemoving] = useState<boolean>(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState<boolean>(false);

  const columns = useClassesBookClassColumns();

  const hasLearningUnits = (data?.courseLearningUnits?.length || 0) > 0;

  const handleAdd = useCallback((classItem: IClassesBookClassItem) => {
    modalAddRef?.current?.open(classItem);
  }, []);
  const handleEdit = useCallback(
    (classItem: IClassesBookClassItem, activity: LessonActivity) => {
      modalAddRef?.current?.open(classItem, activity);
    },
    [],
  );
  const handleProcessRemove = useCallback(
    async (classItem: IClassesBookClassItem, activity: LessonActivity) => {
      setRemoving(true);
      const { error } = await removeSectionClassActivity(
        classItem.sectionId,
        activity.id,
      );
      await refresh();
      setRemoving(false);
      if (!error) {
        addToast({
          text: t(`modalRemove.success`),
          color: 'success',
          icon: 'success',
        });
      } else if (['TEACHER_HASNT_ACCESS'].includes(error?.data?.message)) {
        addToast({
          text: t(`modalRemove.errors.${error}`),
          color: 'danger',
          icon: 'error',
        });
      } else {
        addToast({
          text: t(`modalRemove.failed`),
          color: 'danger',
          icon: 'error',
        });
      }
    },
    [t, refresh],
  );
  const handleRemove = useCallback(
    (classItem: IClassesBookClassItem, activity: LessonActivity) => {
      if (removing) {
        return;
      }
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t(`modalRemove.title`),
        subtitle: t(`modalRemove.subtitle`),
        btnConfirm: {
          text: t(`common:actions.confirm`),
          onConfirm: () => {
            handleProcessRemove(classItem, activity);
          },
        },
        btnClose: {
          text: t(`common:actions.cancel`),
        },
      });
    },
    [t, removing, handleProcessRemove],
  );

  const handleDownloadReport = useCallback(
    async (sectionId: string) => {
      setIsDownloadingPdf(true);
      const resp: Blob | { data: any } = await downloadReport({ sectionId });
      if ('data' in resp && resp.data?.size > 0) {
        const blob = new Blob([resp.data], {
          type: 'application/pdf',
        });
        downloadFromBlob(
          blob,
          `${(data?.details?.courseName ?? '')
            .split(' ')
            .join('_')}_libro_de_clases.pdf`,
        );
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`modalReport.success`),
        });
        setIsDownloadingPdf(false);
        return;
      }
      if ('error' in resp) {
        showDialogInfo({
          icon: {
            name: 'warning',
            color: 'primary',
          },
          title: t(`modalReport.title`),
          subtitle: t(`modalReport.subtitle`),
          btnConfirm: {
            text: t(`modalReport.accept`),
            onConfirm: () => {},
          },
        });
        setIsDownloadingPdf(false);
        return;
      }
      setIsDownloadingPdf(false);
    },
    [t, data],
  );

  const handleDownloadExcel = useCallback(
    async (sectionId: string) => {
      setIsDownloadingExcel(true);
      const resp: Blob | { data: any } = await downloadExcel({ sectionId });
      if ('data' in resp && resp.data?.size > 0) {
        const blob = new Blob([resp.data], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        downloadFromBlob(
          blob,
          `${(data?.details?.courseName ?? '')
            .split(' ')
            .join('_')}_libro_de_clases.xlsx`,
        );
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`modalReport.success`),
        });
        setIsDownloadingExcel(false);
        return;
      }
      if ('error' in resp) {
        showDialogInfo({
          icon: {
            name: 'warning',
            color: 'primary',
          },
          title: t(`modalReport.title`),
          subtitle: t(`modalReport.subtitle`),
          btnConfirm: {
            text: t(`modalReport.accept`),
            onConfirm: () => {},
          },
        });
        setIsDownloadingExcel(false);
        return;
      }
      setIsDownloadingExcel(false);
    },
    [t, data],
  );

  return (
    <>
      <Card className="m-3 py-4">
        <Container fluid>
          <Row>
            <Col
              xs={12}
              md={4}
              lg={3}
              xl={2}
              className="d-flex align-items-center mb-4"
            >
              <GoBackButton />
            </Col>
            <Col xs={12} md={8} lg={9} xl={10} className="mb-4">
              <SectionDetails {...data?.details} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={7}>
              <h2 className="w-full text-center text-md-left text-primary fs-20 fw-700">
                {t(`title`)}
              </h2>
              <p className="w-full text-center text-md-left text-light fs-14">
                {t(`description`)}
              </p>
            </Col>
            <Col
              xs={12}
              md={5}
              className="d-flex align-items-end justify-content-center"
            >
              <div className="flex-fill d-none d-md-block" />
              <Button
                text={t(`classes.download`)}
                icon="download"
                size="md"
                style={{
                  width: '280px',
                  maxWidth: '100%',
                }}
                outlined
                loading={isDownloadingPdf}
                onClick={() => handleDownloadReport(sectionId)}
              />
              <Button
                text={t(`classes.downloadExcel`)}
                icon="download"
                size="md"
                style={{
                  width: '280px',
                  maxWidth: '100%',
                  marginLeft: '8px',
                }}
                outlined
                loading={isDownloadingExcel}
                onClick={() => handleDownloadExcel(sectionId)}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={12}>
              <TableCollapsable
                columns={columns}
                data={data?.classes || []}
                isLoading={loading || removing}
                noResultsText={t('classes.empty.title')}
                secondColumn={{
                  columnName: 'second',
                  headerText: 'second',
                  cellFormat: ({ row }) => (
                    <ClassesBookClassActivities
                      row={row}
                      activitiesLimit={data?.activitiesLimit || undefined}
                      hasLearningUnits={hasLearningUnits}
                      onPressAdd={handleAdd}
                      onPressEdit={handleEdit}
                      onPressRemove={handleRemove}
                    />
                  ),
                }}
              />
            </Col>
          </Row>
        </Container>
      </Card>
      <ModalAddActivity
        ref={modalAddRef}
        units={data?.courseLearningUnits || []}
        onSave={refresh}
      />
    </>
  );
};

export default ClassesBookEdit;
